import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase, SITE_URL } from "@/integrations/supabase/client";
import { formatSupabaseError } from "@/utils/supabaseHelpers";
import { LoaderCircle } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      });

      if (error) {
        console.error('Login error:', error);
        
        if (error.message.includes('Email not confirmed')) {
          toast.error('Please verify your email address before logging in.', {
            action: {
              label: 'Resend',
              onClick: async () => {
                try {
                  const { error: resendError } = await supabase.auth.resend({
                    type: 'signup',
                    email: email.trim(),
                    options: {
                      emailRedirectTo: `${SITE_URL}/login`,
                    }
                  });
                  
                  if (resendError) throw resendError;
                  toast.success('Confirmation email sent! Please check your inbox.');
                } catch (err) {
                  console.error('Error resending confirmation:', err);
                  toast.error('Failed to resend confirmation email. Please try again.');
                }
              }
            },
            duration: 10000,
          });
        } else {
          toast.error(formatSupabaseError(error));
        }
        return;
      }

      if (data?.session?.user) {
        toast.success("Successfully logged in!");
        navigate("/");
      } else {
        throw new Error("No session data received");
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error instanceof Error) {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        
        if (session?.user) {
          navigate("/");
          return;
        }
      } catch (error) {
        console.error("Session check error:", error);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        navigate("/");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" 
         style={{
           backgroundImage: "linear-gradient(135deg, #1EAEDB 0%, #33C3F0 50%, #F2FCE2 100%)",
           backgroundSize: "200% 200%",
           animation: "gradient 15s ease infinite",
         }}>
      <Card className="w-full max-w-md">
        <div className="flex flex-col items-center">
          <img 
            src="/lovable-uploads/c075a6f2-9d57-4c52-9766-e3aa7a5b1c7d.png" 
            alt="Scheduler Logo" 
            className="w-48 h-auto -mt-20 mb-6"
          />
        </div>

        <CardHeader className="space-y-2">
          <CardTitle className="text-center text-2xl font-bold">
            Welcome back
          </CardTitle>
          <CardDescription className="text-center">
            Sign in to your account or{" "}
            <Link to="/signup" className="text-primary hover:text-primary/80">
              create a new account
            </Link>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="block w-full"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="block w-full"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 text-base"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <LoaderCircle className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="text-center text-sm">
            <Link to="/signup" className="text-primary hover:text-primary/80">
              Don't have an account? Sign up
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Login;
